import * as React from "react";
import { css } from "@emotion/core";
import { Alert, Drawer } from "@blueprintjs/core";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthState } from "../../context/auth";
import {
  useArticles,
  useArticle,
  useArticleMutations,
  useSearchArticles,
} from "../../hooks/useKnowledgeBase";
import { useArticleNavigation } from "../../hooks/useArticleNavigation";
import { useArticleEditor } from "../../hooks/useArticleEditor";
import { useArticleSelection } from "../../hooks/useArticleSelection";
import ApiKnowledgeBase, {
  IKnowledgeBaseArticle,
} from "../../api/knowledgeBase";
import ArticleTree from "./Tree";
import ArticleViewer from "./ArticleViewer";
import SearchDialog from "./SearchDialog";

const KnowledgeBase = () => {
  const { currentUser } = useAuthState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isFybrAdmin = currentUser?.organization_role === "fybr_admin";
  const isCreatingRef = React.useRef(false);

  const { data: allArticles, isLoading: articlesLoading } = useArticles();

  const { selectedArticleId, selectArticle, updateHash } = useArticleNavigation(
    allArticles,
    isCreatingRef
  );
  const {
    data: selectedArticle,
    isLoading: articleLoading,
    isFetching: articleFetching,
  } = useArticle(selectedArticleId);
  const { createArticle, updateArticle, deleteArticle } = useArticleMutations();

  const {
    isEditing,
    isCreating,
    editTitle,
    editSlug,
    setEditTitle,
    setEditSlug,
    startEdit,
    cancelEdit,
    startCreate,
    createParentId,
    createPosition,
    setIsEditing,
    setIsCreating,
    setCreateParentId,
    setCreatePosition,
    resetForSelection,
  } = useArticleEditor({
    selectedArticle: selectedArticle || null,
    allArticles,
    selectedArticleId,
    selectArticle,
    updateHash,
    isCreatingRef,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [articleToDelete, setArticleToDelete] = React.useState<number | null>(
    null
  );
  const [isSearchDialogOpen, setIsSearchDialogOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const {
    data: searchResults = [],
    isLoading: isSearching,
    isFetched: hasFetchedSearch,
  } = useSearchArticles(searchQuery, isSearchDialogOpen);

  const getAncestorPath = React.useCallback(
    (articleId: number): Array<string | number> => {
      if (!allArticles || allArticles.length === 0) return [];
      const byId = new Map(allArticles.map((a) => [a.id, a]));
      const expandIds: Array<string | number> = [];
      let currentId: number | null = articleId;

      while (currentId !== null) {
        const article = byId.get(currentId);
        if (!article) break;
        const parentId = article.parent_id;
        if (parentId === null) {
          expandIds.push("root");
          break;
        }
        expandIds.push(parentId);
        currentId = parentId;
      }

      return Array.from(new Set(expandIds));
    },
    [allArticles]
  );

  const articlesById = React.useMemo(
    () =>
      allArticles && allArticles.length > 0
        ? new Map(allArticles.map((a) => [a.id, a]))
        : new Map<number, IKnowledgeBaseArticle>(),
    [allArticles]
  );

  const pendingDeleteArticle = React.useMemo(
    () => allArticles?.find((a) => a.id === articleToDelete),
    [allArticles, articleToDelete]
  );

  const openDeleteDialog = (id: number | null | undefined) => {
    if (!id) return;
    setArticleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const openSearchDialog = () => setIsSearchDialogOpen(true);
  const closeSearchDialog = () => setIsSearchDialogOpen(false);

  const getBreadcrumbTitles = React.useCallback(
    (articleId: number) => {
      if (!articlesById.size) return [];
      const titles: string[] = [];
      let parentId = articlesById.get(articleId)?.parent_id ?? null;

      while (parentId !== null) {
        const parent = articlesById.get(parentId);
        if (!parent) break;
        titles.push(parent.title);
        parentId = parent.parent_id;
      }

      return titles.reverse();
    },
    [articlesById]
  );

  const handleConfirmDelete = () => {
    if (!articleToDelete) return;
    deleteArticle.mutate(articleToDelete, {
      onSuccess: () => {
        handleSelectArticle(null);
        closeDeleteDialog();
      },
    });
  };

  const handleSelectArticle = React.useCallback(
    (articleId: number | null, options?: { fromHash?: boolean }) => {
      resetForSelection();
      selectArticle(articleId, options);
    },
    [resetForSelection, selectArticle]
  );

  const {
    pendingSelectionId: _pendingSelectionId,
    setPendingSelectionId,
    pendingExpandIds,
    setPendingExpandIds,
  } = useArticleSelection(allArticles, handleSelectArticle);

  const handleSearchSelect = React.useCallback(
    (articleId: number) => {
      const expandIds = getAncestorPath(articleId);
      if (expandIds.length > 0) {
        setPendingExpandIds(expandIds);
      }
      setPendingSelectionId(articleId);
      handleSelectArticle(articleId);
      closeSearchDialog();
      setSearchQuery("");
    },
    [
      closeSearchDialog,
      getAncestorPath,
      handleSelectArticle,
      setPendingExpandIds,
      setPendingSelectionId,
    ]
  );

  const handleStartCreate = (parentId?: number | null) => {
    startCreate(parentId);
  };

  const handleSaveArticle = (data: {
    title: string;
    slug: string;
    content: any;
  }) => {
    if (isCreating) {
      createArticle.mutate(
        {
          title: data.title,
          slug: data.slug,
          content: data.content,
          parent_id: createParentId,
          position: createPosition ?? 0,
        },
        {
          onSuccess: (article: any) => {
            setIsCreating(false);
            setIsEditing(false);
            setCreateParentId(null);
            setCreatePosition(null);
            if (article?.id) {
              setPendingSelectionId(article.id);
              if (article.parent_id !== null) {
                setPendingExpandIds([article.parent_id]);
              }
              const nextSlug = article?.slug || data.slug;
              handleSelectArticle(article.id, { fromHash: true });
              updateHash(article.id, { slugOverride: nextSlug });
              setEditSlug(nextSlug);
            }
            setEditTitle(data.title);
          },
        }
      );
      return;
    }

    if (!selectedArticleId) return;

    updateArticle.mutate(
      {
        id: selectedArticleId,
        data: {
          title: data.title,
          slug: data.slug,
          content: data.content,
        },
      },
      {
        onSuccess: (article: any) => {
          setIsEditing(false);
          if (article?.slug) {
            setEditSlug(article.slug);
            updateHash(selectedArticleId, { slugOverride: article.slug });
          } else {
            updateHash(selectedArticleId, { slugOverride: data.slug });
          }
          setEditTitle(data.title);
        },
      }
    );
  };

  const handleEditArticle = () => {
    startEdit();
  };

  const handleCancelEdit = () => {
    cancelEdit();
  };

  const prefetchArticle = React.useCallback(
    (articleId: number) => {
      if (!articleId) return;
      queryClient.prefetchQuery({
        queryKey: ["kb-article", articleId],
        queryFn: () =>
          ApiKnowledgeBase.getArticle(articleId).then((res) => res.article),
      });
    },
    [queryClient]
  );

  return (
    <Drawer
      icon="manual"
      isOpen
      size={`calc(100vh - 66px)`}
      position="bottom"
      className="bp6-dark"
      title="Knowledge Base"
      canOutsideClickClose
      canEscapeKeyClose
      isCloseButtonShown
      onClose={() => navigate(-1)}
    >
      <div
        css={css`
          display: flex;
          height: 100%;
          background: var(--background-gradient);
        `}
      >
        <ArticleTree
          articles={allArticles}
          isLoading={articlesLoading}
          selectedArticleId={selectedArticleId}
          onSelectArticle={handleSelectArticle}
          onCreateArticle={() => handleStartCreate()}
          isFybrAdmin={isFybrAdmin}
          forceExpandIds={pendingExpandIds}
          onHoverArticle={prefetchArticle}
          onOpenSearch={openSearchDialog}
        />
        <div
          css={css`
            flex: 1;
            padding: 2rem 2rem 4rem 2rem;
            overflow-y: auto;
            background-color: var(--neutral-1);
            max-width: 100ch;
          `}
        >
          <ArticleViewer
            article={selectedArticle || null}
            isLoading={!selectedArticle && (articleLoading || articleFetching)}
            isCreating={isCreating}
            isEditing={isEditing}
            isFybrAdmin={isFybrAdmin}
            editTitle={editTitle}
            editSlug={editSlug}
            setEditTitle={setEditTitle}
            setEditSlug={setEditSlug}
            onSave={handleSaveArticle}
            onStartEdit={handleEditArticle}
            onCancelEdit={handleCancelEdit}
            onDelete={() => openDeleteDialog(selectedArticle?.id)}
            isSaving={
              isCreating ? createArticle.isPending : updateArticle.isPending
            }
            isDeleting={deleteArticle.isPending}
          />
        </div>
      </div>
      <SearchDialog
        isOpen={isSearchDialogOpen}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchResults={searchResults}
        allArticles={allArticles}
        isSearching={isSearching}
        hasFetchedSearch={hasFetchedSearch}
        onClose={closeSearchDialog}
        onSelectResult={handleSearchSelect}
        getBreadcrumbTitles={getBreadcrumbTitles}
      />

      <Alert
        isOpen={deleteDialogOpen}
        onCancel={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        intent="danger"
        icon="trash"
        loading={deleteArticle.isPending}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{pendingDeleteArticle?.title || "this article"}</strong>? This
          action cannot be undone.
        </p>
      </Alert>
    </Drawer>
  );
};

export default KnowledgeBase;
